import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts, gradients, shadows, spacing } from "@/lib/theme";

interface Props {
    onAddManually: () => void;
    onAddViaPhoto: () => void;
    onAddViaVoice: () => void;
    onCompleteSection: () => void;
    completeSectionLoading?: boolean;
    /**
     * Disable "Add via Photo" while an extraction is in progress, so the RN
     * can't start a second upload that would race the first (the WS "done" event
     * carries no job id, so two in-flight jobs clobber the hub's single
     * `extracting` state). Dims + relabels the button "Processing…".
     */
    addViaPhotoDisabled?: boolean;
}

export function StickyBottomBar({
    onAddManually,
    onAddViaPhoto,
    onAddViaVoice,
    onCompleteSection,
    completeSectionLoading,
    addViaPhotoDisabled
}: Props) {
    return (
        <View style={ss.container}>
            {/* One button gets its own full-width row. Three outlined buttons
                across a 390pt screen truncates every label to two words, and
                "Add via Voi…" is worse than a second row. */}
            <TouchableOpacity
                style={[ss.btnOutlined, ss.btnFullWidth]}
                onPress={onAddManually}
                activeOpacity={0.7}
            >
                <MaterialCommunityIcons name="pencil-outline" size={17} color={colors.teal500} />
                <Text style={ss.btnOutlinedText}>Add Manually</Text>
            </TouchableOpacity>
            <View style={ss.buttonRow}>
                <TouchableOpacity
                    style={[ss.btnOutlined, addViaPhotoDisabled && ss.btnDisabled]}
                    onPress={onAddViaVoice}
                    disabled={addViaPhotoDisabled}
                    activeOpacity={0.7}
                >
                    <MaterialCommunityIcons
                        name="microphone-outline"
                        size={16}
                        color={addViaPhotoDisabled ? colors.textTertiary : colors.teal500}
                    />
                    <Text style={[ss.btnOutlinedText, addViaPhotoDisabled && ss.btnDisabledText]}>
                        Add via Voice
                    </Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[ss.btnOutlined, addViaPhotoDisabled && ss.btnDisabled]}
                    onPress={onAddViaPhoto}
                    disabled={addViaPhotoDisabled}
                    activeOpacity={0.7}
                >
                    <MaterialCommunityIcons
                        name={addViaPhotoDisabled ? "progress-clock" : "camera-outline"}
                        size={16}
                        color={addViaPhotoDisabled ? colors.textTertiary : colors.teal500}
                    />
                    <Text style={[ss.btnOutlinedText, addViaPhotoDisabled && ss.btnDisabledText]}>
                        {addViaPhotoDisabled ? "Processing…" : "Add via Photo"}
                    </Text>
                </TouchableOpacity>
            </View>
            <TouchableOpacity
                onPress={onCompleteSection}
                disabled={completeSectionLoading}
                activeOpacity={0.8}
            >
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={ss.btnPrimary}
                >
                    <MaterialCommunityIcons name="check-circle-outline" size={18} color="#FFFFFF" />
                    <Text style={ss.btnPrimaryText}>
                        {completeSectionLoading ? "Completing..." : "Complete Section"}
                    </Text>
                </LinearGradient>
            </TouchableOpacity>
        </View>
    );
}

const ss = StyleSheet.create({
    container: {
        backgroundColor: colors.surface,
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.md,
        paddingBottom: 28,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        ...shadows.lg
    },
    buttonRow: {
        flexDirection: "row",
        gap: 10,
        marginBottom: 10
    },
    btnOutlined: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        paddingVertical: 12,
        borderRadius: 9999,
        backgroundColor: colors.surface,
        borderWidth: 1.5,
        borderColor: colors.teal400
    },
    btnOutlinedText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.teal500
    },
    btnFullWidth: {
        // `flex: 0` is load-bearing. `btnOutlined` sets `flex: 1`, which is what
        // makes the two buttons in `buttonRow` share the width evenly. But this
        // button is a direct child of the COLUMN container, where `flex: 1`
        // means `flexBasis: 0` on the HEIGHT axis — the button collapses to its
        // padding and renders as an empty pill with the icon and label clipped
        // away. Resetting flex lets it size to its content and stretch the width.
        flex: 0,
        alignSelf: "stretch",
        marginBottom: 10
    },
    btnDisabled: {
        borderColor: colors.borderStrong,
        backgroundColor: colors.gray100
    },
    btnDisabledText: {
        color: colors.textTertiary
    },
    btnPrimary: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        width: "100%",
        paddingVertical: 14,
        borderRadius: 9999,
        ...shadows.brand
    },
    btnPrimaryText: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    }
});
